import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/services/firestore/collections';
import { isActiveHold } from '@/services/firestore/holdExpiry';
import { withIdConverter } from '@/services/firestore/converters';
import type {
  Appointment,
  AppointmentHold,
} from '@/services/firestore/types';

const appointmentConverter = withIdConverter<Appointment>();
const holdConverter = withIdConverter<AppointmentHold>();

export const APPOINTMENTS_QUERY_KEY = ['appointments'];
export const APPOINTMENT_HOLDS_QUERY_KEY = ['appointmentHolds'];

export async function listAppointmentsForRange(startAt: string, endAt: string) {
  try {
    const appointmentsQuery = query(
      collection(db, COLLECTIONS.APPOINTMENTS).withConverter(appointmentConverter),
      where('startAt', '>=', startAt),
      where('startAt', '<', endAt),
      orderBy('startAt', 'asc'),
    );
    const snapshot = await getDocs(appointmentsQuery);

    return snapshot.docs.map((appointment) => appointment.data());
  } catch (error) {
    console.error('Error listing appointments:', error);
    throw error;
  }
}

export function subscribeAppointmentsForRange(
  startAt: string,
  endAt: string,
  onData: (appointments: Appointment[]) => void,
  onError?: (error: unknown) => void,
) {
  const appointmentsQuery = query(
    collection(db, COLLECTIONS.APPOINTMENTS).withConverter(appointmentConverter),
    where('startAt', '>=', startAt),
    where('startAt', '<', endAt),
    orderBy('startAt', 'asc'),
  );

  return onSnapshot(
    appointmentsQuery,
    (snapshot) => {
      onData(snapshot.docs.map((appointment) => appointment.data()));
    },
    (error) => {
      console.error('Realtime appointments subscription error:', error);
      onError?.(error);
    },
  );
}

export async function listActiveHoldsForRange(startAt: string, endAt: string) {
  try {
    const holdsQuery = query(
      collection(db, COLLECTIONS.APPOINTMENT_HOLDS).withConverter(holdConverter),
      where('startAt', '>=', startAt),
      where('startAt', '<', endAt),
      orderBy('startAt', 'asc'),
    );
    const snapshot = await getDocs(holdsQuery);

    return snapshot.docs.map((hold) => hold.data()).filter(isActiveHold);
  } catch (error) {
    console.error('Error listing appointment holds:', error);
    throw error;
  }
}

export function subscribeActiveHoldsForRange(
  startAt: string,
  endAt: string,
  onData: (holds: AppointmentHold[]) => void,
  onError?: (error: unknown) => void,
) {
  const holdsQuery = query(
    collection(db, COLLECTIONS.APPOINTMENT_HOLDS).withConverter(holdConverter),
    where('startAt', '>=', startAt),
    where('startAt', '<', endAt),
    orderBy('startAt', 'asc'),
  );

  return onSnapshot(
    holdsQuery,
    (snapshot) => {
      const holds = snapshot.docs.map((hold) => hold.data()).filter(isActiveHold);
      onData(holds);
    },
    (error) => {
      console.error('Realtime appointment holds subscription error:', error);
      onError?.(error);
    },
  );
}